import axios from 'axios';
import { BaseApiService } from './base-api.service';
import { ICompanyFont } from '~/types/model.types';
import { AuthenticatedGlobalContextValue } from '~/providers/base-global-context/authenticated-global.context';
import { IS3UploadFileResponse } from '~/types/server.types';
import { TFileToUpload } from '~/types/file-upload.types';

// TODO: This file need to splitted to only S3 adapter and all the rest suppose to be in separated services
export class S3UploadService extends BaseApiService {
    globalContext: AuthenticatedGlobalContextValue;

    constructor(globalContext: AuthenticatedGlobalContextValue) {
        super(globalContext);

        this.globalContext = globalContext;
    }

    async getAwsData() {
        const { apiService, configStore } = this.globalContext;
        const today = new Date();
        const awsPolicyExpirationDate = new Date(
            configStore.config.aws_policy_expiration
        );
        if (
            !configStore.config.aws_signature ||
            awsPolicyExpirationDate < today
        ) {
            const awsData = await apiService.apiRequest<any>({
                baseURL: configStore.apiUrl,
                method: 'get',
                url: `/application/get_aws_policy`
            });

            configStore.config.aws_signature = awsData.signature;
            configStore.config.aws_policy = awsData.policy;
            configStore.config.aws_policy_expiration = awsData.expiration;
            configStore.config.aws_access_key = awsData.aws_access_key;
        }

        return {
            aws_region: `s3.${configStore.config.aws_region}`,
            aws_bucket: configStore.config.aws_bucket,
            aws_access_key: configStore.config.aws_access_key,
            aws_signature: configStore.config.aws_signature,
            aws_policy: configStore.config.aws_policy,
            aws_policy_expiration: configStore.config.aws_policy_expiration
        };
    }

    async uploadFile(
        file: TFileToUpload,
        setUploadProgress: (progress: number, file?: TFileToUpload) => void,
        companyId: string,
        keyPrefix?: string
    ): Promise<IS3UploadFileResponse> {
        const {
            aws_region,
            aws_bucket,
            aws_access_key,
            aws_policy,
            aws_signature
        } = await this.getAwsData();

        const url = `https://${aws_region}.amazonaws.com/${aws_bucket}`;
        const isPrivate = false;
        const contentDisposition = `attachment; filename=${file.name.replace(
            /[^A-Z0-9.-]+/gi,
            '_'
        )}`;

        const formData = new FormData();
        const file_name = file.name;
        const key = `${
            keyPrefix || `companies/${companyId}/uploads`
        }/${new Date().getTime()}/${file_name}`;

        formData.append('utf8', 'true');
        formData.append('Content-Type', file.type);
        formData.append('acl', isPrivate ? 'private' : 'public-read');
        formData.append('key', key);
        formData.append('AWSAccessKeyId', aws_access_key);
        formData.append('Content-Disposition', contentDisposition);
        formData.append('policy', aws_policy);
        formData.append('signature', aws_signature);
        formData.append('success_action_status', '201');
        formData.append('X-Requested-With', 'xhr');
        formData.append('file', file);

        return axios({
            method: 'POST',
            url,
            data: formData,
            headers: { 'Content-Type': 'multipart/form-data' },
            onUploadProgress: ({ loaded, total }) => {
                const progress = (loaded / total) * 100;

                setUploadProgress(progress, file);
            }
        }).then((uploadedFile: any) => {
            uploadedFile.internalId = file._id;
            uploadedFile.file_name = file_name;
            return uploadedFile;
        });
    }

    async uploadFont(file, fileName) {
        const { apiService } = this.globalContext;

        const { currCompanyId } = this.globalContext.sessionStore;

        const presigned_url = await apiService.apiRequest<any>({
            method: 'get',
            url: `/api/v2/companies/${currCompanyId}/font_upload_presigned_url`,
            params: {
                file_name: fileName
            }
        });
        return axios
            .put(presigned_url, file, {
                headers: {
                    'Content-Type': 'application/octet-stream'
                }
            })
            .then(res => {
                return apiService.apiRequest<ICompanyFont>({
                    method: 'put',
                    url: `/api/v2/companies/${currCompanyId}/add_font`,
                    params: {
                        file_name: fileName,
                        version_id: res.headers['x-amz-version-id']
                    }
                });
            });
    }

    async downloadFromS3(uri: string, fileName?: string) {
        const { apiService } = this.globalContext;

        const { resource_url } = await apiService.apiRequest<{
            resource_url: string;
        }>({
            method: 'get',
            url: '/api/v2/content/url',
            params: {
                resource: uri
            },
            responseType: 'json',
            withCredentials: true,
            networkLabel: 'downloadFromS3'
        });
        const tmpDownloadElem = document.createElement('a');
        if (fileName) {
            tmpDownloadElem.download = fileName;
        } else {
            tmpDownloadElem.download = uri.split('/').pop(); // last part of the url
        }
        tmpDownloadElem.href = resource_url;
        tmpDownloadElem.target = '_blank';
        tmpDownloadElem.click();
    }
}
